import Services from 'store/Services';
import { AnyAction, MiddlewareAPI } from 'redux';
import { ThunkDispatch } from 'store/store';
import State from 'store/State';
import {
    OrdersActionType,
    ordersFilterResultsChanged,
    ordersStreamClear,
} from 'app/store/orders/actions';
import { OrderStreamUpdatedEvent } from 'app/store/orders/orderStreamsUpdatedEvent';
import { selectActiveInput } from 'portfolio/store/selectors';
import { OrderFilterResultsChangedEventType } from 'app/store/orders/orderFilterResultsChangedEvent';
import { selectStreamKey } from 'app/store/orders/selectors';
import { dispatchSetPortfolioSelectionIfChanged } from 'portfolio/store/middleware';
import { actionIsPortfolioSetActiveInputAction } from 'portfolio/store/actions';
import { buildLogger } from '@instinet-analytics/core-lib';
import {
    isEmbeddedAppPortfolioAction,
    selectEmbeddedAppOrderIds,
} from 'embedded/store/embeddedAppStateSlice';
import { getEmbeddedAppPortfolioSelection } from 'embedded/EmbeddedAppPortfolioInput';
import { selectConnectivityStatus } from 'app/store/connectivity/selectors';

export const embeddedAppMiddleware = (services: Services) => [
    updatePortfolioOnOrdersReceived(services),
    updatePortfolioOnEmbeddedAppBecomesActiveInput(services),
    updateOrdersStreamAndSelectionOnEmbeddedAppPortfolioUpdated(services),
];

export function updateOrdersStreamAndSelectionOnEmbeddedAppPortfolioUpdated(
    services: Services
) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const {
                loggingService,
                ordersStreamingService,
                streamDataService,
            } = services;
            const logger = buildLogger(
                'updateOrdersStreamAndSelectionOnEmbeddedAppPortfolioUpdated',
                loggingService
            );

            // update the state
            next(action);

            const state = middlewareAPI.getState();

            const activeInput = selectActiveInput(state);

            if (activeInput.activeInputType !== 'Embedded App') {
                return;
            }

            if (!isEmbeddedAppPortfolioAction(action)) {
                return;
            }

            const orderIds = selectEmbeddedAppOrderIds(state);
            const ordersStreamKey = selectStreamKey(state);

            if (orderIds && orderIds.length > 0) {
                logger.debug(
                    'Received new order ids - restarting orders stream',
                    orderIds
                );

                ordersStreamingService.startOrUpdateOrdersStream();
            } else {
                // Ensure orders stream is cleared and stopped
                logger.debug(
                    'No Embedded App order ids - clearing orders and stopping stream',
                    orderIds
                );
                middlewareAPI.dispatch(ordersStreamClear());
                ordersStreamKey &&
                    streamDataService.stopStream(ordersStreamKey);
            }

            // Dispatch the new selection...
            dispatchSelectionAndResetFilter(middlewareAPI, services);
        };
}

/*
 * In an Embedded App we reset the filter results after a new batch of orders are received
 */
export function updatePortfolioOnOrdersReceived(services: Services) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService } = services;

            // update the state
            next(action);

            const state = middlewareAPI.getState();

            const activeInput = selectActiveInput(state);

            if (activeInput.activeInputType !== 'Embedded App') {
                return;
            }

            if (action.type !== OrdersActionType.ORDERS_STREAM_UPDATED) {
                return;
            }
            const orderStreamUpdatedEvent =
                action.payload! as OrderStreamUpdatedEvent;

            if (orderStreamUpdatedEvent.completesRefresh) {
                loggingService.debug(
                    'store|updatePortfolioOnOrdersReceived',
                    'Received new orders => updating portfolio'
                );

                dispatchSelectionAndResetFilter(middlewareAPI, services);
            }
        };
}

export function updatePortfolioOnEmbeddedAppBecomesActiveInput(
    services: Services
) {
    const { loggingService, ordersStreamingService } = services;

    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            // update the state
            next(action);

            if (!actionIsPortfolioSetActiveInputAction(action)) {
                return;
            }

            if (action.payload!.activeInputType !== 'Embedded App') {
                return;
            }

            const logger = buildLogger(
                'store|actionIsPortfolioSetActiveInputAction',
                loggingService
            );

            logger.debug('Newport becoming active input => updating portfolio');

            dispatchSelectionAndResetFilter(middlewareAPI, services);

            const nextState = middlewareAPI.getState();

            if (selectConnectivityStatus(nextState).connected) {
                const orderIds = selectEmbeddedAppOrderIds(nextState);
                if (orderIds && orderIds?.length > 0) {
                    logger.debug(
                        'Connected and have order ids so starting orders stream...'
                    );
                    ordersStreamingService.startOrUpdateOrdersStream();
                }
            }
        };
}

function dispatchSelectionAndResetFilter(
    middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>,
    services: Services
) {
    const nextPortfolioSelection = getEmbeddedAppPortfolioSelection(
        middlewareAPI.getState()
    );
    dispatchSetPortfolioSelectionIfChanged(
        middlewareAPI,
        services,
        nextPortfolioSelection
    );

    middlewareAPI.dispatch(
        ordersFilterResultsChanged({
            changedType: OrderFilterResultsChangedEventType.RESET,
        })
    );
}
